import { DS_COLOR, DS_SPACING_UNITS } from '@calm/evexia-design-tokens';
import { Replay } from '@mui/icons-material';
import { useMediaControls } from '@rhg/hooks/use-media-controls';
import { useNoInteractionsFadeOut } from '@rhg/hooks/use-no-interactions-fade-out';
import playerMessages from '@rhg/lang/messages/player';
import {
    Box,
    Fade,
    IconButton,
    Slider,
    SxProps,
    alpha,
    useTheme,
    useMediaQuery,
} from '@rhg/libs/material';
import {
    PauseFilled,
    PlayFilled,
    VolumeOffFilled,
    VolumeOnFilled,
} from '@rhg/libs/material/icons';
import { sxAppend } from '@rhg/libs/material/sx';
import { displayTime } from '@rhg/utils/media';
import { ReactElement, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { MediaSubtitles } from '../../subtitles';
import { PlayerWithTracks } from '../../types';
import { ControlButtonType } from '../types';

const GRADIENT_OVERLAY_ASPECT_RATIO = 229 / 1440;
const FADE_IN_ANIMATION_DURATION = 500;

export interface VideoMinimalControlsProps {
    mediaEl: PlayerWithTracks;
    sx?: SxProps;
    isInitiallyMuted?: boolean;
    actionButton?: ReactElement;
    shouldInterruptPlay?: boolean;
    onControlClick?: (buttonType: ControlButtonType) => void;
}

const styleVideoControlsOverlay = {
    position: 'absolute',
    width: '100%',
    height: '100%',
    top: 0,
    left: 0,
};

const styleBottomContainer = {
    position: 'absolute',
    bottom: 0,
    left: 0,
    width: '100%',
};

const styleBottomSubtitles = { mb: 2 };

const styleActionFade = { width: '100%' };

const styleControlsContainer = {
    px: 8,
};

const styleControlsAllButtonsContainer: SxProps = {
    width: '100%',
    display: 'flex',
    gap: 4,
    justifyContent: 'flex-end',
    py: 6,
};

const styleControlsCoreButtonsContainer: SxProps = {
    width: {
        xs: '100%',
        sm: '100%',
        md: 'auto',
    },
    display: 'flex',
    gap: 4,
    justifyContent: 'flex-end',
    p: {
        xs: DS_SPACING_UNITS.SIDE_SMALL,
        sm: DS_SPACING_UNITS.SIDE_SMALL,
        md: 0,
    },
    // Adding a zIndex to prevent overlap with header
    position: 'relative',
    zIndex: 2,
};

const styleTimingControl = {
    py: 1,
    mb: 3.25,
    height: 4,
    width: '100%',
    '& .MuiSlider-thumb:not(.MuiSlider-active)': {
        transition: 'left 0.35s linear',
    },
    '& .MuiSlider-track': {
        transition: 'width 0.35s linear',
    },
};

const styleIconButton = {
    background: DS_COLOR.BLACK_60P,
    width: 48,
    height: 48,
    '&:hover': {
        backgroundColor: {
            xs: 'mask.black.60p',
            md: 'mask.white.8p',
        },
    },
};

const styleGradientOverlay = {
    position: 'absolute',
    bottom: 0,
    width: '100%',
    height: `calc(${GRADIENT_OVERLAY_ASPECT_RATIO} * 100dvw)`,
    background: `linear-gradient(180deg, ${alpha(DS_COLOR.WHITE_100P, 0)} 0%, ${
        DS_COLOR.BLACK_100P
    } 100%)`,
};

export function VideoMinimalControls({
    mediaEl,
    isInitiallyMuted,
    sx,
    actionButton,
    shouldInterruptPlay,
    onControlClick,
}: VideoMinimalControlsProps) {
    const { formatMessage } = useIntl();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const {
        currentCue,
        isMuted,
        timingState,
        isPaused,
        togglePlay,
        toggleMuted,
        setTime,
    } = useMediaControls({
        type: 'video',
        mediaEl,
        isInitiallyMuted,
        shouldInterruptPlay,
    });
    const hasNotInteractedInAWhile = useNoInteractionsFadeOut(3000);
    const areSubtitlesUnderScrubber = !hasNotInteractedInAWhile && isMobile;

    const handleTogglePlay = useCallback(() => {
        if (onControlClick) {
            // The reverse of the current state, prior to calling togglePlay
            onControlClick(isPaused ? 'play' : 'pause');
        }

        togglePlay();
    }, [isPaused, onControlClick, togglePlay]);

    const controls = useMemo(() => {
        return (
            <Box sx={styleControlsCoreButtonsContainer}>
                <Fade
                    in={hasNotInteractedInAWhile}
                    timeout={FADE_IN_ANIMATION_DURATION}
                >
                    <IconButton
                        trackButton={
                            timingState.isPaused
                                ? 'VideoPlayerPlay'
                                : 'VideoPlayerPause'
                        }
                        onClick={handleTogglePlay}
                        aria-label={
                            timingState.isPaused
                                ? timingState.currentTime > 0 &&
                                  timingState.remainingTime === 0
                                    ? formatMessage(
                                          playerMessages['player.replay']
                                      )
                                    : formatMessage(
                                          playerMessages['player.play']
                                      )
                                : formatMessage(playerMessages['player.pause'])
                        }
                        sx={styleIconButton}
                    >
                        {timingState.isPaused ? (
                            timingState.currentTime > 0 &&
                            timingState.remainingTime === 0 ? (
                                <Replay />
                            ) : (
                                <PlayFilled />
                            )
                        ) : (
                            <PauseFilled />
                        )}
                    </IconButton>
                </Fade>
                <IconButton
                    trackButton={
                        isMuted ? 'VideoPlayerUnmute' : 'VideoPlayerMute'
                    }
                    onClick={() => toggleMuted()}
                    sx={styleIconButton}
                    aria-label={
                        isMuted
                            ? formatMessage(playerMessages['player.unmute'])
                            : formatMessage(playerMessages['player.mute'])
                    }
                >
                    {isMuted ? <VolumeOffFilled /> : <VolumeOnFilled />}
                </IconButton>
            </Box>
        );
    }, [
        hasNotInteractedInAWhile,
        timingState.isPaused,
        timingState.currentTime,
        timingState.remainingTime,
        handleTogglePlay,
        formatMessage,
        isMuted,
        toggleMuted,
    ]);

    return (
        <Box sx={sxAppend(styleVideoControlsOverlay, sx)}>
            {isMobile && controls}
            <Fade
                in={hasNotInteractedInAWhile}
                timeout={FADE_IN_ANIMATION_DURATION}
            >
                <Box sx={styleGradientOverlay} />
            </Fade>
            <Box sx={styleBottomContainer}>
                {!areSubtitlesUnderScrubber && (
                    <MediaSubtitles cue={currentCue} isMuted={isMuted} />
                )}
                <Box sx={styleControlsContainer}>
                    <Box sx={styleControlsAllButtonsContainer}>
                        {!!actionButton && (
                            <Fade
                                style={isMobile ? styleActionFade : undefined}
                                in={hasNotInteractedInAWhile}
                                timeout={FADE_IN_ANIMATION_DURATION}
                            >
                                <Box>{actionButton}</Box>
                            </Fade>
                        )}
                        {!isMobile && controls}
                    </Box>
                    <Fade
                        in={hasNotInteractedInAWhile}
                        timeout={FADE_IN_ANIMATION_DURATION}
                    >
                        <Slider
                            sx={styleTimingControl}
                            value={timingState.currentTime}
                            max={timingState.duration}
                            aria-label={formatMessage(
                                playerMessages['player.seek_slider']
                            )}
                            getAriaValueText={(sliderValue) =>
                                formatMessage(
                                    playerMessages['player.current_time'],
                                    {
                                        time: displayTime(sliderValue),
                                    }
                                )
                            }
                            step={timingState.duration / 1000}
                            size="small"
                            onChangeCommitted={(_, newTime) => {
                                setTime(newTime as number);
                            }}
                        />
                    </Fade>
                    {areSubtitlesUnderScrubber && (
                        <MediaSubtitles
                            cue={currentCue}
                            isMuted={isMuted}
                            sx={styleBottomSubtitles}
                        />
                    )}
                </Box>
            </Box>
        </Box>
    );
}
